/**
 * DashboardLayoutRoute — the shell for everything behind sign-in.
 *
 * The frame itself — sidebar that owns the full height, icon-rail collapse
 * (remembered per browser), the mobile Sheet, the header row — is uikit's
 * <AppShell>. This file only supplies what is specific to the app: the nav
 * registry, the user menu, theme controls and sign-out.
 *
 * ── The guard lives here, not in each page ────────────────────────────────
 * <AuthGuard> wraps the shell, so every page under /dashboard is protected by
 * one check. Put it in the pages instead and each new file is a chance to
 * forget — the kind of omission nothing catches until it is public.
 *
 * Because this is a ROUTE, the shell mounts once: sidebar state and any
 * provider mounted here survive navigation between dashboard pages.
 */
import * as React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  AppShell, Button,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  Tooltip, TooltipTrigger, TooltipContent,
  type AppShellLinkProps,
} from '@bloomneo/uikit';
import { LogOut, ChevronDown } from 'lucide-react';
import { brand } from './brand';
import { ThemeControls } from './ThemeControls';
import { AuthGuard } from '@/features/auth/components';
import { useAuth } from '@/features/auth/hooks';
import type { NavItem } from './nav.base';

/*
 * Nav is COLLECTED, not declared here.
 *
 * `import.meta.glob` with `eager` is resolved by Vite at BUILD time, so this
 * is a static import list by the time it runs — no runtime lookup, and a layer
 * that is not installed contributes nothing because its file does not exist.
 *
 * Sorting by `order` keeps the sidebar stable regardless of the order the
 * bundler happens to hand back the modules, which is not guaranteed.
 */
const navModules = import.meta.glob<{ items?: NavItem[] }>('./nav.*.ts', { eager: true });

const NAV: NavItem[] = Object.keys(navModules)
  .sort()
  .flatMap((k) => navModules[k].items ?? [])
  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/** AppShell is router-agnostic; this hands it react-router's <Link>. */
function RouterLink({ href, ...rest }: AppShellLinkProps) {
  return <Link to={href} {...rest} />;
}

function UserMenu() {
  const { user, logout } = useAuth();
  const initials = (user?.name ?? user?.email ?? 'U').slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 gap-2 pl-1.5">
          <span className="grid size-6 place-items-center rounded-full bg-primary text-[11px] font-medium text-primary-foreground">
            {initials}
          </span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="truncate font-normal text-muted-foreground">
          {user?.email ?? 'Signed in'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild><Link to="/dashboard/profile">Profile</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/">Back to site</Link></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()}>
          <LogOut className="size-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/*
 * Sign out sits at the bottom of the sidebar, away from navigation — it is a
 * different kind of action and should not be one slip from "Users". Collapsed
 * to the icon rail, the label leaves the DOM and a tooltip names it instead.
 */
function SignOut({ collapsed }: { collapsed: boolean }) {
  const { logout } = useAuth();
  const button = (
    <button
      onClick={() => logout()}
      aria-label={collapsed ? 'Sign out' : undefined}
      className={[
        'flex w-full cursor-pointer items-center rounded-md py-2 text-sm text-sidebar-foreground/75 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        collapsed ? 'justify-center px-0' : 'gap-3 px-3',
      ].join(' ')}
    >
      <LogOut className="size-4 shrink-0" />
      {!collapsed && 'Sign out'}
    </button>
  );
  if (!collapsed) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">Sign out</TooltipContent>
    </Tooltip>
  );
}

export function Shell() {
  const { user } = useAuth();
  const { pathname } = useLocation();

  // `role` hides an item from anyone without it. Presentation only: the
  // layout's AuthGuard and the API's own checks are the actual gates.
  const nav = React.useMemo(
    () =>
      NAV.filter((item) => !item.role || item.role === user?.role).map(({ to, label, icon, end, section }) => ({
        href: to,
        label,
        icon,
        end,
        section,
      })),
    [user?.role],
  );

  return (
    <AppShell
      brand={{ name: brand.name, href: '/dashboard', mark: brand.shortName.charAt(0) }}
      nav={nav}
      navLabel="Dashboard"
      currentPath={pathname}
      linkComponent={RouterLink}
      // Kept from the pre-6.0 shell so an existing collapse preference survives.
      storageKey="sidebar:collapsed"
      headerActions={
        <>
          <ThemeControls />
          <UserMenu />
        </>
      }
      sidebarFooter={(collapsed) => <SignOut collapsed={collapsed} />}
    >
      <Outlet />
    </AppShell>
  );
}

export function DashboardLayoutRoute() {
  // One guard for every page in the section.
  return (
    <AuthGuard>
      <Shell />
    </AuthGuard>
  );
}
